import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Text,
  Badge,
  Skeleton,
  HStack,
} from '@chakra-ui/react'
import { ArrowUp, ArrowDown, Minus } from 'lucide-react'

interface ModuleAnalytics {
  module: string
  today: number
  this_week: number
  this_month: number
}

interface ModuleAnalyticsTableProps {
  analytics: ModuleAnalytics[]
  isLoading?: boolean
}

export function ModuleAnalyticsTable({ analytics, isLoading = false }: ModuleAnalyticsTableProps) {
  const calculateWeekChange = (today: number, week: number) => {
    if (week === 0) return today > 0 ? 100 : 0
    return ((today * 7 - week) / week) * 100
  }

  const calculateMonthChange = (week: number, month: number) => {
    if (month === 0) return week > 0 ? 100 : 0
    return ((week * 4 - month) / month) * 100
  }

  const getChangeIcon = (change: number) => {
    if (change > 5) return <ArrowUp size={14} />
    if (change < -5) return <ArrowDown size={14} />
    return <Minus size={14} />
  }

  const getChangeColor = (change: number) => {
    if (change > 5) return 'green.500'
    if (change < -5) return 'red.500'
    return 'gray.500'
  }

  if (isLoading) {
    return (
      <Card bg="white" borderRadius="md" boxShadow="sm">
        <CardHeader>
          <Heading size="md">Module Analytics</Heading>
        </CardHeader>
        <CardBody>
          <Skeleton height="300px" width="100%" />
        </CardBody>
      </Card>
    )
  }

  if (!analytics || analytics.length === 0) {
    return (
      <Card bg="white" borderRadius="md" boxShadow="sm">
        <CardHeader>
          <Heading size="md">Module Analytics</Heading>
        </CardHeader>
        <CardBody>
          <Box textAlign="center" py={8}>
            <Text color="gray.500">No module analytics data available</Text>
          </Box>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card bg="white" borderRadius="md" boxShadow="sm">
      <CardHeader>
        <Heading size="md">Module Analytics</Heading>
        <Text fontSize="sm" color="gray.500">
          Activity breakdown by module (Today, This Week, This Month)
        </Text>
      </CardHeader>
      <CardBody pt={0} overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Module</Th>
              <Th isNumeric>Today</Th>
              <Th isNumeric>This Week</Th>
              <Th isNumeric>This Month</Th>
              <Th>Trend</Th>
            </Tr>
          </Thead>
          <Tbody>
            {analytics.map((module, index) => {
              const weekChange = calculateWeekChange(module.today, module.this_week)
              const monthChange = calculateMonthChange(module.this_week, module.this_month)

              return (
                <Tr key={module.module || index}>
                  <Td fontWeight="medium">{module.module || 'Unknown'}</Td>
                  <Td isNumeric>
                    <Text fontWeight="bold">{module.today.toLocaleString()}</Text>
                  </Td>
                  <Td isNumeric>
                    <Text>{module.this_week.toLocaleString()}</Text>
                  </Td>
                  <Td isNumeric>
                    <Text>{module.this_month.toLocaleString()}</Text>
                  </Td>
                  <Td>
                    <HStack spacing={1} justify="flex-start">
                      {getChangeIcon(weekChange)}
                      <Text fontSize="xs" color={getChangeColor(weekChange)}>
                        {weekChange > 0 ? '+' : ''}{weekChange.toFixed(0)}%
                      </Text>
                    </HStack>
                  </Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </CardBody>
    </Card>
  )
}
